/**
 * Fish Data Viewer - Admin tool for reviewing merged fish species data
 * Hidden page (not in navigation) for validating merge quality
 */
import { useState, useMemo, useEffect } from 'react';
import {
  Container,
  Title,
  Stack,
  Paper,
  Alert,
  Table,
  ScrollArea,
  Text,
  TextInput,
  Group,
  Loader,
  Center,
  Badge,
  Anchor,
  Checkbox,
  Pagination,
  Select,
  ActionIcon,
  Tooltip,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconAlertCircle, IconSearch, IconExternalLink, IconFilter, IconUnlink, IconGitMerge } from '@tabler/icons-react';
import { useFishViewer } from '@/hooks/useFishViewer';
import { fishSpeciesApi } from '@/api/fishSpecies';
import type { FishViewerEntry } from '@/types/livestock';
import { UnmergePanel } from '@/components/FishMerge/UnmergePanel';
import { MergePanel } from '@/components/FishMerge/MergePanel';
import { useAuth } from '@/contexts/AuthContext';

const PAGE_SIZE = 50;

// Source link component with external icon
function SourceLink({ url, id, label }: { url: string | null; id: number | null; label: string }) {
  if (!url || !id) return <Text size="xs" c="dimmed">-</Text>;
  return (
    <Anchor href={url} target="_blank" rel="noopener noreferrer" size="xs">
      {label} #{id} <IconExternalLink size={12} style={{ verticalAlign: 'middle' }} />
    </Anchor>
  );
}

// Source cell showing common name, scientific name, and link
function SourceCell({
  commonName,
  scientificName,
  url,
  id,
  sourceLabel,
  canUnmerge,
  onUnmerge,
}: {
  commonName: string | null;
  scientificName: string | null;
  url: string | null;
  id: number | null;
  sourceLabel: string;
  canUnmerge?: boolean;
  onUnmerge?: () => void;
}) {
  if (!id) {
    return (
      <Stack gap={0}>
        <Text size="xs" c="dimmed">Not found</Text>
      </Stack>
    );
  }
  return (
    <Stack gap={2}>
      <Group gap={4} justify="space-between" wrap="nowrap">
        <Text size="sm" fw={500} lineClamp={1}>{commonName || '-'}</Text>
        {canUnmerge && onUnmerge && (
          <Tooltip label="Unmerge this source">
            <ActionIcon size="xs" variant="subtle" color="red" onClick={onUnmerge} aria-label="Unmerge this source">
              <IconUnlink size={12} />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>
      <Text size="xs" c="dimmed" fs="italic" lineClamp={1}>{scientificName || '-'}</Text>
      <SourceLink url={url} id={id} label={sourceLabel} />
    </Stack>
  );
}

export default function FishViewerPage() {
  const { user, isLoading: authLoading } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery] = useDebouncedValue(searchQuery, 300);
  const [unmergedOnly, setUnmergedOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [categories, setCategories] = useState<string[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [unmergeTarget, setUnmergeTarget] = useState<{
    canonicalId: number;
    sourceName: string;
  } | null>(null);
  const [mergeSourceId, setMergeSourceId] = useState<number | null>(null);

  // Check admin access (computed by backend from FISH_ADMIN_EMAILS env var)
  const isAdmin = user?.is_fish_admin ?? false;

  // Load categories on mount (must be before early returns - Rules of Hooks)
  useEffect(() => {
    // Only load if admin
    if (!isAdmin) return;
    setCategoriesLoading(true);
    fishSpeciesApi.getCategories()
      .then((response) => {
        // Extract category names from response objects
        setCategories(response.categories.map(c => c.name));
      })
      .catch((err) => {
        console.error('Failed to load categories:', err);
      })
      .finally(() => {
        setCategoriesLoading(false);
      });
  }, [isAdmin]);

  // Reset page when filters change
  const filters = useMemo(() => ({
    q: debouncedQuery || undefined,
    category: selectedCategory || undefined,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
    unmerged_only: unmergedOnly || undefined,
  }), [debouncedQuery, selectedCategory, page, unmergedOnly]);

  const { data, isLoading, error, refetch } = useFishViewer(filters);

  const totalPages = data ? Math.ceil(data.total_count / PAGE_SIZE) : 0;

  // Show loading while checking auth
  if (authLoading) {
    return (
      <Container size="xl" py="xl">
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      </Container>
    );
  }

  // Redirect non-admin users
  if (!isAdmin) {
    return (
      <Container size="xl" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Access Denied"
          color="red"
        >
          You do not have permission to access the Fish Data Viewer.
          This page is restricted to administrators only.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Title order={1}>Fish Data Viewer</Title>
        <Text c="dimmed" size="sm">
          Admin tool for reviewing merged fish species data. Search by common or scientific name.
          IDs shown can be used for feedback on merge/unmerge decisions.
        </Text>

        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            title="Error loading fish data"
            color="red"
          >
            {error instanceof Error ? error.message : 'An unexpected error occurred'}
          </Alert>
        )}

        <Paper p="md" withBorder>
          <Stack gap="md">
            {/* Search and filters */}
            <Group>
              <TextInput
                placeholder="Search by name..."
                leftSection={<IconSearch size={16} />}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
                style={{ flex: 1, maxWidth: 400 }}
              />
              <Select
                placeholder="Filter by category..."
                data={categories.map((cat) => ({ value: cat, label: cat }))}
                value={selectedCategory}
                onChange={(value) => {
                  setSelectedCategory(value);
                  setPage(1);
                }}
                clearable
                searchable
                leftSection={<IconFilter size={16} />}
                disabled={categoriesLoading}
                style={{ minWidth: 200 }}
              />
              <Checkbox
                label="Unmerged only"
                checked={unmergedOnly}
                onChange={(e) => {
                  setUnmergedOnly(e.target.checked);
                  setPage(1);
                }}
              />
            </Group>

            {/* Results count */}
            {data && (
              <Text size="sm" c="dimmed">
                Showing {data.entries.length} of {data.total_count} entries
                {data.query && ` matching "${data.query}"`}
              </Text>
            )}

            {/* Data Table */}
            {isLoading ? (
              <Center py="xl">
                <Loader size="lg" />
              </Center>
            ) : (
              <ScrollArea>
                <Table striped highlightOnHover withTableBorder withColumnBorders>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ minWidth: 60 }}>ID</Table.Th>
                      <Table.Th style={{ minWidth: 150 }}>Canonical Name</Table.Th>
                      <Table.Th style={{ minWidth: 80 }}>Sources</Table.Th>
                      <Table.Th style={{ minWidth: 200 }}>LiveAquaria</Table.Th>
                      <Table.Th style={{ minWidth: 200 }}>AbyssAquatics</Table.Th>
                      <Table.Th style={{ minWidth: 200 }}>TMC</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {data?.entries && data.entries.length > 0 ? (
                      data.entries.map((entry: FishViewerEntry, index: number) => (
                        <Table.Tr key={entry.canonical_id || `row-${index}`}>
                          <Table.Td>
                            <Text size="xs" fw={500}>
                              {entry.canonical_id || '-'}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Stack gap={2}>
                              <Group gap={4} justify="space-between" wrap="nowrap">
                                <Text size="sm" fw={500} lineClamp={1}>
                                  {entry.common_name || '-'}
                                </Text>
                                <Tooltip label="Merge into another canonical">
                                  <ActionIcon
                                    size="xs"
                                    variant="subtle"
                                    color="blue"
                                    onClick={() => entry.canonical_id && setMergeSourceId(entry.canonical_id)}
                                    disabled={!entry.canonical_id}
                                    aria-label="Merge into another canonical"
                                  >
                                    <IconGitMerge size={12} />
                                  </ActionIcon>
                                </Tooltip>
                              </Group>
                              <Text size="xs" c="dimmed" fs="italic" lineClamp={1}>
                                {entry.scientific_name || '-'}
                              </Text>
                              {entry.category && (
                                <Badge size="xs" variant="light">
                                  {entry.category}
                                </Badge>
                              )}
                            </Stack>
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              color={entry.source_count >= 3 ? 'green' : entry.source_count === 2 ? 'yellow' : 'gray'}
                              variant="filled"
                            >
                              {entry.source_count}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <SourceCell
                              commonName={entry.la_common_name}
                              scientificName={entry.la_scientific_name}
                              url={entry.la_source_url}
                              id={entry.la_id}
                              sourceLabel="LA"
                              canUnmerge={entry.source_count > 1 && entry.canonical_id !== null && entry.la_id !== null}
                              onUnmerge={() => setUnmergeTarget({
                                canonicalId: entry.canonical_id!,
                                sourceName: 'LiveAquaria'
                              })}
                            />
                          </Table.Td>
                          <Table.Td>
                            <SourceCell
                              commonName={entry.aa_common_name}
                              scientificName={entry.aa_scientific_name}
                              url={entry.aa_source_url}
                              id={entry.aa_id}
                              sourceLabel="AA"
                              canUnmerge={entry.source_count > 1 && entry.canonical_id !== null && entry.aa_id !== null}
                              onUnmerge={() => setUnmergeTarget({
                                canonicalId: entry.canonical_id!,
                                sourceName: 'AbyssAquatics'
                              })}
                            />
                          </Table.Td>
                          <Table.Td>
                            <SourceCell
                              commonName={entry.tmc_common_name}
                              scientificName={entry.tmc_scientific_name}
                              url={entry.tmc_source_url}
                              id={entry.tmc_id}
                              sourceLabel="TMC"
                              canUnmerge={entry.source_count > 1 && entry.canonical_id !== null && entry.tmc_id !== null}
                              onUnmerge={() => setUnmergeTarget({
                                canonicalId: entry.canonical_id!,
                                sourceName: 'TMC'
                              })}
                            />
                          </Table.Td>
                        </Table.Tr>
                      ))
                    ) : (
                      <Table.Tr>
                        <Table.Td colSpan={6}>
                          <Center py="xl">
                            <Text c="dimmed">
                              {debouncedQuery
                                ? 'No fish found matching your search'
                                : 'No fish data available. Run the merge script first.'}
                            </Text>
                          </Center>
                        </Table.Td>
                      </Table.Tr>
                    )}
                  </Table.Tbody>
                </Table>
              </ScrollArea>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <Center>
                <Pagination
                  total={totalPages}
                  value={page}
                  onChange={setPage}
                  withEdges
                />
              </Center>
            )}
          </Stack>
        </Paper>
      </Stack>

      <UnmergePanel
        opened={unmergeTarget !== null}
        onClose={() => setUnmergeTarget(null)}
        canonicalId={unmergeTarget?.canonicalId ?? 0}
        sourceToUnmerge={unmergeTarget?.sourceName ?? ''}
        onSuccess={() => {
          setUnmergeTarget(null);
          refetch();
        }}
      />

      <MergePanel
        opened={mergeSourceId !== null}
        onClose={() => setMergeSourceId(null)}
        sourceCanonicalId={mergeSourceId ?? 0}
        onSuccess={() => {
          setMergeSourceId(null);
          refetch();
        }}
      />
    </Container>
  );
}
